import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

/**
 * Tilt3D - wraps existing content in a 3D-tilting card. Purely presentational: it does not
 * change, remove or duplicate whatever is passed as children, it only adds motion/depth around it.
 *
 * Usage:  <Tilt3D><YourExistingCardMarkup /></Tilt3D>
 *
 * Props:
 *   max     - max tilt angle in degrees (default 10)
 *   scale   - hover scale (default 1.02)
 *   glare   - show a moving light sheen over the card (default true)
 *   as      - wrapper element type, e.g. 'a' (default 'div')
 *   className / style / ...rest - passed to the wrapper element (so existing card classes like
 *             "svc-card" / "director-card" keep working unchanged)
 */
export default function Tilt3D({ children, max = 10, scale = 1.02, glare = true, as = 'div', className = '', style = {}, ...rest }) {
  const ref = useRef(null);
  const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const springRx = useSpring(rx, { stiffness: 220, damping: 22, mass: 0.6 });
  const springRy = useSpring(ry, { stiffness: 220, damping: 22, mass: 0.6 });
  const gx = useMotionValue(50);
  const gy = useMotionValue(50);
  const glareBg = useTransform([gx, gy], ([x, y]) => `radial-gradient(circle at ${x}% ${y}%, rgba(255,255,255,0.35), rgba(255,255,255,0) 55%)`);

  const handleMove = (e) => {
    if (reduceMotion) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;   // 0..1
    const py = (e.clientY - rect.top) / rect.height;    // 0..1
    ry.set((px - 0.5) * max * 2);   // left/right tilt
    rx.set((0.5 - py) * max * 2);   // up/down tilt
    gx.set(px * 100);
    gy.set(py * 100);
  };
  const handleLeave = () => { rx.set(0); ry.set(0); gx.set(50); gy.set(50); };

  const MotionTag = motion[as] || motion.div;

  return (
    <MotionTag
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      whileHover={reduceMotion ? undefined : { scale }}
      style={{
        rotateX: reduceMotion ? 0 : springRx,
        rotateY: reduceMotion ? 0 : springRy,
        transformStyle: 'preserve-3d',
        transformPerspective: 900,
        position: 'relative',
        ...style,
      }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      className={`tilt-3d ${className}`}
      {...rest}
    >
      {children}
      {glare && !reduceMotion && (
        <motion.span
          aria-hidden="true"
          className="tilt-3d-glare"
          style={{ background: glareBg }}
        />
      )}
    </MotionTag>
  );
}
